import { useState, useCallback } from 'react';
import { coursesApi } from '../../courses/services/coursesApi';

/**
 * @typedef {'EXPLAIN' | 'EXAMPLE' | 'DEEPER' | 'WHY' | 'HOW' | 'CUSTOM'} QuestionType
 */

/**
 * @typedef {'initial' | 'expanded' | 'loading' | 'custom' | 'response'} PopupState
 */

/**
 * Hook for managing Ask Graspd state and API interactions.
 *
 * @param {{
 *   courseId: string,
 *   lessonId: string,
 *   sectionId: string,
 *   selectedText: string,
 *   startOffset: number,
 *   endOffset: number,
 *   onSuccess?: (item: { id: string, selectedText: string, startOffset: number, endOffset: number, questionType: string, customQuestion?: string, answer: string, createdAt: string }) => void
 * }} params
 */
export function useAskGraspd({ courseId, lessonId, sectionId, selectedText, startOffset, endOffset, onSuccess }) {
  /** @type {[PopupState, Function]} */
  const [state, setState] = useState('initial');
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [questionType, setQuestionType] = useState(null);

  const askQuestion = useCallback(async (type, customQuestion = null) => {
    if (!courseId || !lessonId || !sectionId || !selectedText) {
      setError('Missing required context');
      return;
    }

    setState('loading');
    setError(null);
    setQuestionType(type);

    try {
      const data = await coursesApi.askGraspd({
        courseId,
        lessonId,
        sectionId,
        selectedText,
        startOffset,
        endOffset,
        questionType: type,
        customQuestion: type === 'CUSTOM' ? customQuestion : undefined,
        auth: true,
      });

      setResponse(data.answer);
      setState('response');

      // Call onSuccess callback with the full response data for history
      if (onSuccess) {
        onSuccess({
          id: data.id,
          selectedText: data.selectedText || selectedText,
          startOffset: data.startOffset ?? startOffset,
          endOffset: data.endOffset ?? endOffset,
          questionType: data.questionType || type,
          customQuestion: type === 'CUSTOM' ? customQuestion : undefined,
          answer: data.answer,
          createdAt: data.createdAt || new Date().toISOString(),
        });
      }
    } catch (err) {
      console.error('[useAskGraspd] Error:', err);

      // Map error status to user-friendly messages
      let message = 'Something went wrong. Please try again.';
      if (err?.status === 400) {
        message = 'Please select between 10-2000 characters.';
      } else if (err?.status === 403) {
        message = "You don't have access to this course.";
      } else if (err?.status === 429) {
        message = 'Please wait before asking another question.';
      } else if (err?.status === 503) {
        message = 'Unable to generate response. Please try again.';
      } else if (err?.message) {
        message = err.message;
      }

      setError(message);
      setState('expanded'); // Go back to expanded to show error
    }
  }, [courseId, lessonId, sectionId, selectedText, startOffset, endOffset, onSuccess]);

  const expand = useCallback(() => {
    setState('expanded');
    setError(null);
  }, []);

  const showCustomInput = useCallback(() => {
    setState('custom');
    setError(null);
  }, []);

  const reset = useCallback(() => {
    setState('expanded');
    setResponse(null);
    setError(null);
    setQuestionType(null);
  }, []);

  const goBack = useCallback(() => {
    if (state === 'custom') {
      setState('expanded');
    } else if (state === 'response') {
      setState('expanded');
      setResponse(null);
    }
  }, [state]);

  return {
    // State
    state,
    response,
    error,
    questionType,

    // State checks
    isInitial: state === 'initial',
    isExpanded: state === 'expanded',
    isLoading: state === 'loading',
    isCustom: state === 'custom',
    showResponse: state === 'response',

    // Actions
    expand,
    showCustomInput,
    askQuestion,
    reset,
    goBack,
  };
}
